/**
 * Talksession process fields the homepage query used to omit.
 * ListingPage must infer these instead of showing the generic invalid-data page.
 */
import { TALK_SESSION_LISTING_TYPE } from './sessionPricing';

export const TALK_SESSION_TRANSACTION_PROCESS_ALIAS = 'default-booking/release-1';
export const TALK_SESSION_UNIT_TYPE = 'fixed';

/**
 * Listing-type config row for a publicData.listingType value.
 *
 * @param {string} [listingType]
 * @param {Object} [listingConfig]
 * @returns {Object|undefined}
 */
export const listingTypeConfigFor = (listingType, listingConfig) =>
  (listingConfig?.listingTypes || []).find(conf => conf.listingType === listingType);

/**
 * Fill listingType / transactionProcessAlias / unitType from the listing
 * type config or the locked talksession defaults. Missing optional fields
 * (payoutsReady, topics, price variants, availability) are ignored.
 *
 * @param {Object} [publicData]
 * @param {Object} [listingConfig]
 * @returns {{ listingType: string|null, transactionProcessAlias: string|null, unitType: string|null }}
 */
export const inferListingProcessFields = (publicData = {}, listingConfig) => {
  const rawType = publicData?.listingType;
  const listingType = rawType || TALK_SESSION_LISTING_TYPE;
  const typeConfig = listingTypeConfigFor(listingType, listingConfig);
  const transactionProcessAlias =
    publicData?.transactionProcessAlias ||
    typeConfig?.transactionType?.alias ||
    (listingType === TALK_SESSION_LISTING_TYPE ? TALK_SESSION_TRANSACTION_PROCESS_ALIAS : null);
  const unitType =
    publicData?.unitType ||
    typeConfig?.transactionType?.unitType ||
    (listingType === TALK_SESSION_LISTING_TYPE ? TALK_SESSION_UNIT_TYPE : null);
  return { listingType, transactionProcessAlias, unitType };
};

/**
 * True when ListingPage can render this listing after process inference.
 *
 * @param {Object} [listing]
 * @param {Object} [listingConfig]
 * @returns {boolean}
 */
export const listingCanRenderOnListingPage = (listing, listingConfig) => {
  if (listing?.attributes?.state && listing.attributes.state !== 'published') {
    return false;
  }
  if (listing?.attributes?.deleted) {
    return false;
  }
  const inferred = inferListingProcessFields(listing?.attributes?.publicData || {}, listingConfig);
  return !!(inferred.listingType && inferred.transactionProcessAlias && inferred.unitType);
};

/**
 * Overlay inferred process fields onto publicData for booking / schema.
 *
 * @param {Object} [publicData]
 * @param {Object} [listingConfig]
 * @returns {Object}
 */
export const publicDataWithInferredProcess = (publicData = {}, listingConfig) => ({
  ...publicData,
  ...inferListingProcessFields(publicData, listingConfig),
});
